import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { apiRoutes } from '../constants/api-routes.constants';
import { map, Observable } from 'rxjs';
import {
  AssociatedClassification,
  AssociatedStandard,
  IPatchStandardGroupAssociations,
  IStandardGroup,
  StandardGroupAssociatedRecordScheduledRemoval,
  StandardGroupOwnerEnum
} from './models/standard-groups/standard-groups';
import { StandardGroupTreeDto } from '@components/folder-tree/folder-tree/FolderTreeRootNode';
import { folderTreeArrayMapper } from '@shared/utils/helpers';
import { Classification, Standard } from '../../shared/models/standards/standard';
import { FolderTreeRootNode } from '@shared/models/folder-tree/folder-tree.model';
import { Lookup } from './models/shared/lookup';

@Injectable({
  providedIn: 'root'
})
export class StandardGroupsService {
  getRouterQueryParams(standardGroup: StandardGroupTreeDto) {
    return { queryParams: { id: standardGroup.id } };
  }

  get(id: string): Observable<IStandardGroup[]> {
    return this.http.get<IStandardGroup[]>(apiRoutes.standardGroup.get(id));
  }

  getStandardGroups() {
    return this.http.get<IStandardGroup[]>(apiRoutes.standardGroup.getStandardGroups());
  }

  getStandardSubGroups() {
    return this.http.get<IStandardGroup[]>(apiRoutes.standardGroup.getStandardSubGroups());
  }

  getTree(): Observable<FolderTreeRootNode<StandardGroupTreeDto>[]> {
    return this.http
      .get<FolderTreeRootNode<StandardGroupTreeDto>[]>(apiRoutes.standardGroup.getTree())
      .pipe(map(folderTreeArrayMapper<StandardGroupTreeDto>(StandardGroupTreeDto)));
  }

  create(model: IStandardGroup): Observable<IStandardGroup> {
    return this.http.post<IStandardGroup>(apiRoutes.standardGroup.create(), model);
  }

  update(model: IStandardGroup): Observable<IStandardGroup> {
    return this.http.put<IStandardGroup>(apiRoutes.standardGroup.update(model.id!), model);
  }

  updateAssociatedStandards(model: IPatchStandardGroupAssociations) {
    return this.http.patch<IStandardGroup>(apiRoutes.standardGroup.setAssociatedStandards(model.id), model);
  }

  updateAssociatedClassifications(model: IPatchStandardGroupAssociations) {
    return this.http.patch<IStandardGroup>(apiRoutes.standardGroup.setAssociatedClassifications(model.id), model);
  }

  setAssociatedStandardScheduledRemoval(model: StandardGroupAssociatedRecordScheduledRemoval) {
    return this.http.post<AssociatedStandard>(apiRoutes.standardGroup.setAssociatedStandardScheduledRemoval(), model);
  }

  setAssociatedClassificationScheduledRemoval(model: StandardGroupAssociatedRecordScheduledRemoval) {
    return this.http.post<AssociatedClassification>(
      apiRoutes.standardGroup.setAssociatedClassificationScheduledRemoval(),
      model
    );
  }

  addRecordViewHistory(model: IStandardGroup): Observable<IStandardGroup> {
    return this.http.post<IStandardGroup>(apiRoutes.standardGroup.addRecordViewHistory(model.id!), model);
  }

  getAssociatedClassificationRecords(recordNo: string): Observable<Classification[]> {
    return this.http.get<Classification[]>(apiRoutes.standardGroup.classificationAssociations(recordNo)).pipe(
      map(data => {
        return data.map(item => new Classification(item));
      })
    );
  }

  getAssociatedStandardRecords(stdGroupId: string): Observable<Standard[]> {
    return this.http.get<Standard[]>(apiRoutes.standard.standardsForStandardGroup(stdGroupId)).pipe(
      map(data => {
        return data.map(item => new Standard(item));
      })
    );
  }

  constructor(private readonly http: HttpClient) {}

  private readonly groupOwners: Lookup<number>[] = [
    {
      text: 'Industry training organisation',
      value: StandardGroupOwnerEnum.IndustryTrainingOrganisation
    },
    {
      text: 'Secondary education group',
      value: StandardGroupOwnerEnum.SecondaryEducationGroup
    },
    {
      text: 'Tertiary education group',
      value: StandardGroupOwnerEnum.TertiaryEducationGroup
    },
    {
      text: 'Not applicable',
      value: StandardGroupOwnerEnum.NotApplicable
    }
  ];

  getGroupOwners(): Lookup<number>[] {
    return this.groupOwners;
  }

  mapStandardGroupsOption(standardGroups: IStandardGroup[]): Lookup<string>[] {
    return standardGroups
      .filter((standardGroup): standardGroup is typeof standardGroup & { id: string } => standardGroup.id != null)
      .map(standardGroup => ({
        value: standardGroup.id,
        text: standardGroup.title
      }));
  }
}
